import { type FC, useEffect, useMemo, useRef, useState } from 'react';

// Open all links inside the previewed HTML in a new tab.
const iframeBaseTarget = '<base target="_blank">';

const autoHeightLayoutStyle = 'html,body{margin:0;overflow:hidden}';

interface Props {
    value?: Nullable<string>;
    title?: string;
    className?: string;
    styleContent?: string;
    autoHeight?: boolean;
    minHeight?: number;
}

const HtmlPreview: FC<Props> = ({
    value,
    title = 'html-preview',
    className,
    styleContent,
    autoHeight = false,
    minHeight = 0,
}) => {
    const srcDoc = useMemo(() => {
        const styles = [styleContent, autoHeight && autoHeightLayoutStyle]
            .filter(Boolean)
            .map((css) => `<style>${css}</style>`)
            .join('');

        return `${iframeBaseTarget}${styles}${value ?? ''}`;
    }, [styleContent, autoHeight, value]);

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [height, setHeight] = useState(minHeight);

    useEffect(() => {
        if (!autoHeight) return;

        const iframe = iframeRef.current;
        if (!iframe) return;

        let resizeObserver: Nullable<ResizeObserver> = null;
        let rafId = 0;

        const measure = () => {
            const doc = iframe.contentDocument;
            const body = doc?.body;
            const html = doc?.documentElement;
            if (!body || !html) return;

            const contentHeight = Math.max(body.scrollHeight, body.offsetHeight, html.scrollHeight, html.offsetHeight);
            const next = Math.max(minHeight, contentHeight);
            setHeight((prev) => (prev === next ? prev : next));
        };

        const handleLoad = () => {
            measure();

            if (!('ResizeObserver' in window)) return;

            let prevWidth = Math.round(iframe.getBoundingClientRect().width);
            resizeObserver = new ResizeObserver((entries) => {
                const width = Math.round(entries[0].contentRect.width);
                if (width === prevWidth) return;
                prevWidth = width;
                cancelAnimationFrame(rafId);
                rafId = requestAnimationFrame(measure);
            });
            resizeObserver.observe(iframe);
        };

        iframe.addEventListener('load', handleLoad);

        if (iframe.contentDocument?.readyState === 'complete') {
            handleLoad();
        }

        return () => {
            cancelAnimationFrame(rafId);
            iframe.removeEventListener('load', handleLoad);
            resizeObserver?.disconnect();
        };
    }, [autoHeight, minHeight, srcDoc]);

    return (
        <iframe
            ref={iframeRef}
            className={className}
            title={title}
            srcDoc={srcDoc}
            sandbox="allow-same-origin allow-popups"
            scrolling={autoHeight ? 'no' : undefined}
            style={autoHeight ? { height: `${height}px`, overflow: 'hidden' } : undefined}
        />
    );
};

export default HtmlPreview;
